import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeftRight,
  ArrowRight,
  Banknote,
  Building2,
  CalendarClock,
  Check,
  CreditCard,
  Minus,
  Plus,
  ScanLine,
  Search,
  Trash2,
  User,
  X,
} from 'lucide-react'
import { useDatos, type EntradaVenta } from '../store/datos'
import { useSesion, usuarioActual } from '../store/sesion'
import { useUI } from '../store/ui'
import { Pagina } from '../components/Layout'
import { Avatar, Boton, Campo, Chip, Entrada, Tarjeta } from '../components/ui/base'
import { Hoja } from '../components/ui/Hoja'
import { EscanerCodigo } from '../components/Escaner'
import { BotonSimulador, SimuladorPrecios } from '../components/SimuladorPrecios'
import { IconoCanastoParticular, IconoPuestoEmpresa } from '../components/IconosVenta'
import { cn } from '../lib/cn'
import {
  ESTADO_STOCK_META,
  estadoStock,
  fecha as formatoFecha,
  hoyISO,
  money,
  normalizar,
  num,
  pesoLabel,
  relativo,
} from '../lib/format'
import { vigentes } from '../lib/metricas'
import { proximoDia } from '../lib/geo'
import {
  conIva,
  esVolumen,
  kilosLinea,
  montoIva,
  precioLista,
  precioMinimoLinea,
  tienePrecioPropio,
} from '../lib/precios'
import {
  DIAS_DESPACHO,
  METODOS_PAGO,
  type DiaDespacho,
  type EstadoPedido,
  type MetodoPago,
  type TipoCliente,
} from '../types'

interface LineaCarro {
  productoId: string
  cantidad: number
  precioUnitario: number
}

const ICONO_PAGO = {
  efectivo: Banknote,
  transferencia: ArrowLeftRight,
  tarjeta: CreditCard,
  credito: CalendarClock,
}

function tipoDeParam(v: string | null): TipoCliente | null {
  return v === 'empresa' || v === 'particular' ? v : null
}

export function Vender() {
  const { productos, clientes, ventas, ajustes } = useDatos()
  const registrarVenta = useDatos((s) => s.registrarVenta)
  const guardarCliente = useDatos((s) => s.guardarCliente)
  const esAdmin = useSesion((s) => s.usuario?.rol === 'admin')
  const avisar = useUI((s) => s.avisar)
  const navegar = useNavigate()
  const [params] = useSearchParams()

  const [clienteId, setClienteId] = useState(params.get('cliente') ?? '')

  // Al volver de la plataforma de Clientes (elegido o recién creado),
  // React Router no remonta esta página —solo cambia el "?cliente=" en
  // la misma ruta—, así que hay que escuchar el cambio a mano en vez de
  // confiar en el valor inicial del useState de arriba.
  useEffect(() => {
    const id = params.get('cliente')
    if (id) setClienteId(id)
  }, [params])

  const [carro, setCarro] = useState<LineaCarro[]>([])
  const [metodoPago, setMetodoPago] = useState<MetodoPago>('efectivo')
  // Toda venta nueva parte "en preparación": así entra sola a Despachos
  // con su día de reparto, que es lo normal —el vendedor toma el pedido
  // y otro lo entrega—. Si además lo entregó en el momento, marca
  // "Entregado" a mano y el pedido no pasa por la ruta.
  const [estado, setEstado] = useState<EstadoPedido>('preparando')
  const [observaciones, setObservaciones] = useState('')
  // Cada cuántos días este cliente vuelve a pedir, y hasta cuándo tiene
  // plazo de pago si la venta queda a crédito: ambos se guardan aparte
  // de la venta en sí, así que se editan con inputs propios.
  const [frecuenciaPedido, setFrecuenciaPedido] = useState('')
  const [vencimientoPago, setVencimientoPago] = useState('')
  // Ruta de despacho: martes, jueves, o un día especial elegido a mano.
  const [diaDespacho, setDiaDespacho] = useState<DiaDespacho>('martes')
  const [fechaEspecial, setFechaEspecial] = useState('')
  // Mientras el vendedor no elija el día a mano, se lo sugiere solo
  // según el peso del pedido (política: menos de 3 kilos va el jueves).
  // Apenas toca un botón, se respeta su elección para el resto de esta
  // venta.
  const [diaTocado, setDiaTocado] = useState(false)

  const [panelProducto, setPanelProducto] = useState(false)
  const [escaner, setEscaner] = useState(false)
  const [confirmando, setConfirmando] = useState(false)

  const cliente = clientes.find((c) => c.id === clienteId)

  // A quién le estamos vendiendo define TODA la pantalla: los precios
  // que se proponen, el piso de venta y si el total lleva IVA. Se elige
  // en la portada (queda en la URL) y, si se llega con un cliente ya
  // puesto, se deduce de él.
  const tipo = tipoDeParam(params.get('tipo')) ?? cliente?.tipo ?? null
  const esEmpresa = tipo === 'empresa'
  const iva = ajustes.porcentajeIva || 19

  // Al elegir un cliente, se precarga la frecuencia que ya tenía
  // guardada (si nunca se le puso una, el campo queda vacío).
  useEffect(() => {
    setFrecuenciaPedido(cliente?.frecuenciaPedidoDias ? String(cliente.frecuenciaPedidoDias) : '')
  }, [cliente?.id, cliente?.frecuenciaPedidoDias])

  // La fecha de pago solo tiene sentido si es a crédito; se propone un
  // plazo por defecto (el de Ajustes) pero queda libre para editar.
  useEffect(() => {
    if (metodoPago !== 'credito') {
      setVencimientoPago('')
      return
    }
    setVencimientoPago((actual) => {
      if (actual) return actual
      const d = new Date()
      d.setDate(d.getDate() + ajustes.diasCredito)
      return d.toISOString().slice(0, 10)
    })
  }, [metodoPago, ajustes.diasCredito])

  const ultimaVentaCliente = useMemo(() => {
    if (!clienteId) return undefined
    return vigentes(ventas)
      .filter((v) => v.clienteId === clienteId)
      .sort((a, b) => b.fecha.localeCompare(a.fecha))[0]
  }, [ventas, clienteId])

  const lineas = useMemo(
    () =>
      carro
        .map((l) => {
          const p = productos.find((x) => x.id === l.productoId)
          return p ? { ...l, producto: p, subtotal: l.cantidad * l.precioUnitario } : null
        })
        .filter((l): l is NonNullable<typeof l> => l !== null),
    [carro, productos],
  )

  const total = lineas.reduce((s, l) => s + l.subtotal, 0)
  const costo = lineas.reduce((s, l) => s + l.producto.precioCosto * l.cantidad, 0)
  const utilidad = total - costo

  const kilosTotales = useMemo(
    () => lineas.reduce((s, l) => s + kilosLinea(l.cantidad, l.producto), 0),
    [lineas],
  )

  // Sugerencia automática del día: pedidos livianos (menos de 3 kilos)
  // van el jueves, el resto va el martes. Se apaga apenas el vendedor
  // toca un botón a mano.
  useEffect(() => {
    if (diaTocado || lineas.length === 0) return
    setDiaDespacho(kilosTotales < 3 ? 'jueves' : 'martes')
  }, [kilosTotales, lineas.length, diaTocado])

  const agregar = (productoId: string) => {
    const p = productos.find((x) => x.id === productoId)
    if (!p) return
    const disponible = p.stock - p.reservado
    if (disponible <= 0) {
      avisar(`${p.nombre} está sin stock.`, 'error')
      return
    }
    setCarro((c) => {
      const existente = c.find((l) => l.productoId === productoId)
      if (existente) {
        if (existente.cantidad >= disponible) {
          avisar(`Solo quedan ${disponible} de ${p.nombre}.`, 'error')
          return c
        }
        return c.map((l) => (l.productoId === productoId ? { ...l, cantidad: l.cantidad + 1 } : l))
      }
      // El precio con el que entra la línea es el de la lista que le
      // toca a este cliente: el mayorista si es empresa, el de detalle
      // si es particular. Sigue siendo editable.
      return [...c, { productoId, cantidad: 1, precioUnitario: precioLista(p, tipo ?? 'particular') }]
    })
    navigator.vibrate?.(15)
  }

  const cambiarCantidad = (productoId: string, delta: number) => {
    const p = productos.find((x) => x.id === productoId)
    if (!p) return
    const disponible = p.stock - p.reservado
    setCarro((c) =>
      c
        .map((l) => {
          if (l.productoId !== productoId) return l
          const nueva = l.cantidad + delta
          if (nueva > disponible) {
            avisar(`Solo quedan ${disponible} de ${p.nombre}.`, 'error')
            return l
          }
          return { ...l, cantidad: nueva }
        })
        .filter((l) => l.cantidad > 0),
    )
  }

  const fijarCantidad = (productoId: string, cantidad: number) => {
    const p = productos.find((x) => x.id === productoId)
    if (!p) return
    const tope = p.stock - p.reservado
    setCarro((c) =>
      c.map((l) =>
        l.productoId === productoId ? { ...l, cantidad: Math.max(1, Math.min(cantidad, tope)) } : l,
      ),
    )
  }

  const fijarPrecio = (productoId: string, precio: number) =>
    setCarro((c) =>
      c.map((l) => (l.productoId === productoId ? { ...l, precioUnitario: Math.max(0, precio) } : l)),
    )

  const quitar = (productoId: string) =>
    setCarro((c) => c.filter((l) => l.productoId !== productoId))

  const confirmar = async () => {
    if (!clienteId) {
      avisar(esEmpresa ? 'Elige la empresa a la que le vendes.' : 'Elige el cliente.', 'error')
      return
    }
    if (!carro.length) {
      avisar('Agrega al menos un producto.', 'error')
      setPanelProducto(true)
      return
    }
    if (estado !== 'entregada' && diaDespacho === 'especial' && !fechaEspecial) {
      avisar('Elige la fecha del primer despacho.', 'error')
      return
    }
    setConfirmando(true)
    try {
      const entrada: EntradaVenta = {
        clienteId,
        lineas: carro,
        metodoPago,
        estado,
        observaciones: observaciones.trim() || undefined,
        vencimientoPago: metodoPago === 'credito' ? vencimientoPago || undefined : undefined,
        diaDespacho,
        fechaDespacho: diaDespacho === 'especial' ? fechaEspecial || undefined : proximoDia(diaDespacho === 'martes' ? 2 : 4),
      }
      const venta = await registrarVenta(entrada, usuarioActual())

      // La frecuencia de pedido vive en el cliente, no en la venta: se
      // guarda aparte, y solo si de verdad cambió. Va en su propio
      // try/catch a propósito — la venta ya quedó registrada, así que
      // si esto falla (por ejemplo, la columna todavía no existe en la
      // base de datos) no debe verse como si la venta hubiera fallado.
      const nuevaFrecuencia = Number(frecuenciaPedido) || undefined
      if (cliente && nuevaFrecuencia !== cliente.frecuenciaPedidoDias) {
        try {
          await guardarCliente({ ...cliente, frecuenciaPedidoDias: nuevaFrecuencia }, usuarioActual())
        } catch {
          avisar('La venta quedó registrada, pero no se pudo guardar la frecuencia de pedido.', 'error')
        }
      }

      avisar(`Venta ${venta.folio} registrada por ${money(venta.total)}`)
      navegar(`/ventas/${venta.id}`)
    } catch (err) {
      avisar(err instanceof Error ? err.message : 'No se pudo registrar la venta.', 'error')
    } finally {
      setConfirmando(false)
    }
  }

  // Antes de todo hay que saber a qué lista de precios se vende. Sin
  // eso la pantalla de venta no significa nada: el mismo kilo vale
  // distinto para una pastelería que para una persona.
  if (!tipo) {
    return (
      <PortadaVenta
        onElegir={(t) => {
          setCarro([])
          navegar(`/vender?tipo=${t}`)
        }}
      />
    )
  }

  return (
    <>
      <Pagina className="pb-48 lg:pb-8">
        {/* Recordatorio permanente de con qué lista se está vendiendo:
            es lo que explica todos los precios de más abajo. */}
        <div className="mb-4 flex items-center gap-3 rounded-2xl superficie-elevada border borde-suave p-3 sm:p-3.5">
          <span
            className={cn(
              'grid size-10 shrink-0 place-items-center rounded-xl',
              esEmpresa
                ? 'bg-tinta-50 text-tinta-600 dark:bg-tinta-500/15 dark:text-tinta-200'
                : 'bg-magenta-50 text-magenta-600 dark:bg-magenta-500/15 dark:text-magenta-300',
            )}
          >
            {esEmpresa ? <Building2 className="size-5" /> : <User className="size-5" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-bold texto-base">
              {esEmpresa ? 'Venta a empresa' : 'Venta a particular'}
            </p>
            <p className="text-[12px] texto-suave">
              {esEmpresa
                ? `Precios mayoristas, netos. El total con IVA (${iva}%) va abajo.`
                : 'Precios al detalle.'}
            </p>
          </div>
          <button
            onClick={() => {
              setClienteId('')
              setCarro([])
              navegar('/vender')
            }}
            className="shrink-0 rounded-xl px-3 py-2 text-[13px] font-semibold text-bosque-500 transition-colors hover:superficie-hundida dark:text-bosque-200"
          >
            Cambiar
          </button>
        </div>

        {/* min-w-0 en ambas columnas a propósito: sin eso, un nombre de
            cliente largo o su RUT/comuna/teléfono en una sola línea
            (con "truncate") igual empuja el ancho de la columna del
            grid más allá de la pantalla, porque el track "1fr" no se
            achica bajo el contenido si no se le dice explícitamente
            —el clásico "grid blowout" de CSS. Con min-w-0 el texto se
            corta con "…" como corresponde en vez de ensanchar la
            página entera. */}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
          <div className="min-w-0 space-y-4">
            {/* Paso 1 — Cliente */}
            <Tarjeta className="p-4 sm:p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-[15px] font-bold texto-base">
                  {esEmpresa ? 'Empresa' : 'Cliente'}
                </h3>
                {cliente && (
                  <button
                    onClick={() => navegar(`/clientes?paraVenta=${cliente.tipo}`)}
                    className="text-[13px] font-semibold text-bosque-500 hover:underline dark:text-bosque-200"
                  >
                    Cambiar
                  </button>
                )}
              </div>

              {cliente ? (
                <div className="flex items-center gap-3.5 rounded-2xl superficie-hundida p-3.5">
                  <Avatar nombre={cliente.nombre} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-bold texto-base">
                      {[cliente.nombre, cliente.apellido].filter(Boolean).join(' ')}
                    </p>
                    <p className="truncate text-[13px] texto-suave">
                      {[cliente.rut, cliente.comuna, cliente.telefono].filter(Boolean).join(' · ') ||
                        'Sin datos de contacto'}
                    </p>
                  </div>
                  <Chip
                    className={
                      cliente.tipo === 'empresa'
                        ? 'bg-tinta-50 text-tinta-600 dark:bg-tinta-500/15 dark:text-tinta-200'
                        : 'bg-magenta-50 text-magenta-600 dark:bg-magenta-500/15 dark:text-magenta-300'
                    }
                  >
                    {cliente.tipo === 'empresa' ? 'Empresa' : 'Particular'}
                  </Chip>
                </div>
              ) : null}

              {cliente && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Campo etiqueta="Fecha de la última venta a este cliente">
                    <div className="flex h-12 items-center rounded-2xl border borde-base superficie-hundida px-4 text-[14px] font-semibold texto-base">
                      {ultimaVentaCliente
                        ? `${formatoFecha(ultimaVentaCliente.fecha)} · ${relativo(ultimaVentaCliente.fecha)}`
                        : 'Todavía no le has vendido'}
                    </div>
                  </Campo>
                  <Campo
                    etiqueta="Frecuencia de pedido (días)"
                    ayuda="Cada cuánto vuelve a pedir. Ej: si pide cada 7 días, escribe 7."
                  >
                    <Entrada
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={frecuenciaPedido}
                      onChange={(e) => setFrecuenciaPedido(e.target.value)}
                      placeholder="Ej: 7"
                    />
                  </Campo>
                </div>
              )}

              {/* Elegir cliente abre la plataforma de Clientes ya
                  filtrada a este grupo —el mismo lugar donde se
                  administran, no una lista aparte que se desactualiza. */}
              {!cliente && (
                <button
                  onClick={() => navegar(`/clientes?paraVenta=${tipo}`)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-2xl border-2 border-dashed borde-base p-4 text-left transition-colors',
                    esEmpresa ? 'hover:border-tinta-400' : 'hover:border-magenta-400',
                  )}
                >
                  <span
                    className={cn(
                      'grid size-11 shrink-0 place-items-center rounded-2xl',
                      esEmpresa
                        ? 'bg-tinta-50 text-tinta-600 dark:bg-tinta-500/15 dark:text-tinta-200'
                        : 'bg-magenta-50 text-magenta-600 dark:bg-magenta-500/15 dark:text-magenta-300',
                    )}
                  >
                    <Search className="size-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold texto-base">
                      {esEmpresa ? 'Elegir la empresa' : 'Elegir el cliente'}
                    </span>
                    <span className="block text-[12px] texto-suave">
                      {esEmpresa
                        ? 'Pastelerías, hoteles y restoranes registrados'
                        : 'Personas registradas · también puedes crear una nueva'}
                    </span>
                  </span>
                </button>
              )}
            </Tarjeta>

            {/* Paso 2 — Productos */}
            <Tarjeta className="overflow-hidden">
              <div className="flex items-center justify-between px-4 py-4 sm:px-5">
                <h3 className="text-[15px] font-bold texto-base">
                  Productos
                  {lineas.length > 0 && (
                    <span className="ml-2 text-[13px] font-semibold texto-tenue">
                      {lineas.length} {lineas.length === 1 ? 'ítem' : 'ítems'}
                    </span>
                  )}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEscaner(true)}
                    className="grid size-10 place-items-center rounded-xl superficie-hundida texto-suave transition-colors hover:texto-base"
                    aria-label="Escanear producto"
                  >
                    <ScanLine className="size-[18px]" />
                  </button>
                  <Boton tamano="sm" icono={<Plus className="size-4" />} onClick={() => setPanelProducto(true)}>
                    Agregar
                  </Boton>
                </div>
              </div>

              {lineas.length === 0 ? (
                <button
                  onClick={() => setPanelProducto(true)}
                  className="mx-4 mb-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-2xl border-2 border-dashed borde-base p-4 text-left transition-colors hover:border-bosque-400 sm:mx-5 sm:w-[calc(100%-2.5rem)]"
                >
                  <span className="grid size-11 shrink-0 place-items-center rounded-2xl superficie-hundida texto-tenue">
                    <Plus className="size-5" />
                  </span>
                  <span className="text-[15px] font-semibold texto-suave">
                    Agregar el primer producto
                  </span>
                </button>
              ) : (
                <ul className="divide-y borde-suave">
                  {lineas.map((l) => {
                    const restante = l.producto.stock - l.producto.reservado - l.cantidad
                    const minimo = precioMinimoLinea(l.cantidad, l.producto, tipo)
                    const esPrecioVolumen = esVolumen(l.cantidad, l.producto, tipo)
                    return (
                      <li key={l.productoId} className="p-4 sm:px-5">
                        <div className="flex items-start gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[15px] font-bold texto-base">
                              {l.producto.nombre}
                            </p>
                            <p className="mt-0.5 text-[12px] texto-suave">
                              <span className="codigo">{l.producto.codigo}</span>
                              {l.producto.peso > 0 && ` · ${pesoLabel(l.producto)}`}
                              {' · quedan '}
                              <span
                                className={cn(
                                  'font-semibold',
                                  restante <= 0 ? 'text-rojo-500' : 'texto-suave',
                                )}
                              >
                                {num(restante)}
                              </span>
                            </p>
                          </div>
                          <button
                            onClick={() => quitar(l.productoId)}
                            className="grid size-8 shrink-0 place-items-center rounded-lg texto-tenue transition-colors hover:bg-rojo-50 hover:text-rojo-500 dark:hover:bg-rojo-500/10"
                            aria-label={`Quitar ${l.producto.nombre}`}
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          {/* Contador grande: se usa con el pulgar. */}
                          <div className="flex items-center gap-1 rounded-2xl superficie-hundida p-1">
                            <button
                              onClick={() => cambiarCantidad(l.productoId, -1)}
                              className="grid size-10 place-items-center rounded-xl superficie-elevada texto-base transition-transform active:scale-90"
                              aria-label="Restar uno"
                            >
                              <Minus className="size-4" />
                            </button>
                            <input
                              value={l.cantidad}
                              onChange={(e) => fijarCantidad(l.productoId, Number(e.target.value) || 1)}
                              inputMode="numeric"
                              className="cifra w-12 bg-transparent text-center text-[17px] font-bold texto-base outline-none"
                              aria-label="Cantidad"
                            />
                            <button
                              onClick={() => cambiarCantidad(l.productoId, 1)}
                              className="grid size-10 place-items-center rounded-xl superficie-elevada texto-base transition-transform active:scale-90"
                              aria-label="Sumar uno"
                            >
                              <Plus className="size-4" />
                            </button>
                          </div>

                          <label className="flex flex-col gap-1">
                            <span className="flex items-center gap-2">
                              <span className="text-[12px] font-semibold texto-suave">Precio</span>
                              <input
                                value={l.precioUnitario}
                                onChange={(e) => fijarPrecio(l.productoId, Number(e.target.value) || 0)}
                                inputMode="numeric"
                                className={cn(
                                  'cifra h-10 w-24 rounded-xl border superficie-elevada px-3 text-[15px] font-semibold texto-base outline-none focus:border-bosque-400',
                                  minimo != null && l.precioUnitario < minimo
                                    ? 'border-rojo-400'
                                    : 'borde-base',
                                )}
                              />
                            </span>
                            {(() => {
                              if (minimo == null) return null
                              const bajoMinimo = l.precioUnitario < minimo
                              return (
                                <span
                                  className={cn(
                                    'text-[11px] font-semibold',
                                    bajoMinimo ? 'text-rojo-500' : 'texto-tenue',
                                  )}
                                >
                                  mínimo {money(minimo)}
                                  {esPrecioVolumen && ' · +10 kg'}
                                </span>
                              )
                            })()}
                          </label>

                          <span className="ml-auto text-right">
                            <span className="cifra block text-[17px] font-bold texto-base">
                              {money(l.subtotal)}
                            </span>
                            {esEmpresa && (
                              <span className="cifra block text-[11px] texto-tenue">
                                con IVA {money(conIva(l.subtotal, iva))}
                              </span>
                            )}
                          </span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </Tarjeta>

            {/* Paso 3 — Pago */}
            <Tarjeta className="p-4 sm:p-5">
              <h3 className="mb-3 text-[15px] font-bold texto-base">Forma de pago</h3>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {METODOS_PAGO.map((m) => {
                  const Icono = ICONO_PAGO[m.valor]
                  const activo = metodoPago === m.valor
                  return (
                    <button
                      key={m.valor}
                      onClick={() => setMetodoPago(m.valor)}
                      className={cn(
                        'flex flex-col items-center gap-2 rounded-2xl border-2 p-3.5 transition-all duration-200 ease-[var(--ease-suave)]',
                        activo
                          ? 'border-bosque-500 bg-bosque-50 text-bosque-600 dark:bg-bosque-500/15 dark:text-bosque-200'
                          : 'borde-base texto-suave hover:superficie-hundida',
                      )}
                    >
                      <Icono className="size-5" strokeWidth={2.3} />
                      <span className="text-[13px] font-bold">{m.etiqueta}</span>
                    </button>
                  )
                })}
              </div>

              {metodoPago === 'credito' && (
                <div className="mt-3 anim-aparecer">
                  <p className="rounded-2xl bg-ambar-50 px-4 py-3 text-[13px] font-medium text-ambar-600 dark:bg-ambar-500/10 dark:text-ambar-300">
                    Queda registrada como pendiente de pago. La verás en Cobranza hasta que se marque
                    como pagada.
                  </p>
                  <div className="mt-3">
                    <Campo etiqueta="Fecha en que el cliente debe pagar">
                      <Entrada
                        type="date"
                        min={hoyISO()}
                        value={vencimientoPago}
                        onChange={(e) => setVencimientoPago(e.target.value)}
                      />
                    </Campo>
                  </div>
                </div>
              )}

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Campo
                  etiqueta="Estado del pedido"
                  ayuda={
                    estado === 'entregada'
                      ? 'Ya se lo entregaste al cliente: no entra a la hoja de despachos.'
                      : 'Entra a la hoja del despachador con el día que elijas abajo.'
                  }
                >
                  <div className="grid grid-cols-2 gap-2">
                    {(
                      [
                        ['entregada', 'Entregado'],
                        ['preparando', 'En preparación'],
                      ] as const
                    ).map(([valor, etiqueta]) => (
                      <button
                        key={valor}
                        onClick={() => setEstado(valor)}
                        className={cn(
                          'rounded-xl border px-3 py-2.5 text-[13px] font-semibold transition-colors',
                          estado === valor
                            ? 'border-bosque-500 bg-bosque-50 text-bosque-600 dark:bg-bosque-500/15 dark:text-bosque-200'
                            : 'borde-base texto-suave hover:superficie-hundida',
                        )}
                      >
                        {etiqueta}
                      </button>
                    ))}
                  </div>
                </Campo>

                <Campo etiqueta="Observaciones" ayuda="Opcional. Se ve en la ficha de la venta.">
                  <Entrada
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="Entregar antes de las 09:00"
                  />
                </Campo>
              </div>

              {estado !== 'entregada' && (
                <div className="mt-4 anim-aparecer">
                  <Campo
                    etiqueta="Día de despacho"
                    ayuda={
                      kilosTotales > 0
                        ? `Entra a la ruta del despachador ese día. Este pedido pesa ${kilosTotales < 1 ? `${Math.round(kilosTotales * 1000)} g` : `${num(kilosTotales)} kg`} —${
                            diaTocado ? '' : ' se sugirió solo,'
                          } menos de 3 kg va el jueves.`
                        : 'Entra a la ruta del despachador ese día. Pedidos de menos de 3 kg van el jueves.'
                    }
                  >
                    <div className="grid grid-cols-3 gap-2">
                      {DIAS_DESPACHO.map((d) => (
                        <button
                          key={d.valor}
                          onClick={() => {
                            setDiaDespacho(d.valor)
                            setDiaTocado(true)
                          }}
                          className={cn(
                            'rounded-xl border px-3 py-2.5 text-[13px] font-semibold transition-colors',
                            diaDespacho === d.valor
                              ? 'border-bosque-500 bg-bosque-50 text-bosque-600 dark:bg-bosque-500/15 dark:text-bosque-200'
                              : 'borde-base texto-suave hover:superficie-hundida',
                          )}
                        >
                          {d.etiqueta}
                        </button>
                      ))}
                    </div>
                  </Campo>

                  {diaDespacho === 'especial' && (
                    <div className="mt-4">
                      <Campo etiqueta="Fecha del primer despacho" requerido>
                        <Entrada
                          type="date"
                          min={hoyISO()}
                          value={fechaEspecial}
                          onChange={(e) => setFechaEspecial(e.target.value)}
                        />
                      </Campo>
                    </div>
                  )}

                  {!cliente?.direccion && (
                    <p className="mt-4 rounded-2xl bg-ambar-50 px-4 py-3 text-[13px] font-medium text-ambar-600 dark:bg-ambar-500/10 dark:text-ambar-300">
                      Este cliente no tiene dirección guardada. Agrégala en su ficha para que
                      aparezca en la hoja de ruta del despachador.
                    </p>
                  )}
                </div>
              )}
            </Tarjeta>
          </div>

          {/* Resumen — barra fija en teléfono, columna en escritorio */}
          <div className="min-w-0 lg:sticky lg:top-24">
            <Tarjeta className="hidden p-5 lg:block">
              <h3 className="mb-4 text-[15px] font-bold texto-base">Resumen</h3>
              <Resumen
                total={total}
                costo={esAdmin ? costo : undefined}
                utilidad={esAdmin ? utilidad : undefined}
                items={lineas.length}
                iva={esEmpresa ? iva : undefined}
              />
              <Boton
                tamano="lg"
                bloque
                className="mt-5"
                cargando={confirmando}
                onClick={confirmar}
                icono={<Check className="size-5" />}
              >
                Confirmar venta
              </Boton>
            </Tarjeta>
          </div>
        </div>
      </Pagina>

      {/* Barra de confirmación en teléfono: el total siempre visible.
          Va pegada arriba del menú flotante de abajo —no encima—, así
          que su posición depende de la misma altura segura que usa
          ese menú (env(safe-area-inset-bottom)) más el alto del menú. */}
      <div
        className="fixed inset-x-0 z-30 border-t borde-suave superficie-vidrio px-4 py-3 backdrop-blur-xl lg:hidden"
        style={{ bottom: 'calc(6rem + env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold texto-tenue uppercase">
              {esEmpresa ? 'Total neto' : 'Total'}
            </p>
            <p className="cifra font-display text-2xl leading-tight font-bold texto-base">
              {money(total)}
            </p>
            {esEmpresa && total > 0 && (
              <p className="cifra text-[11px] texto-tenue">
                con IVA {money(conIva(total, iva))}
              </p>
            )}
          </div>
          <Boton
            tamano="lg"
            className="ml-auto flex-1"
            cargando={confirmando}
            onClick={confirmar}
            icono={<Check className="size-5" />}
          >
            Confirmar
          </Boton>
        </div>
      </div>

      {/* Selector de producto */}
      <SelectorProducto
        abierto={panelProducto}
        onCerrar={() => setPanelProducto(false)}
        enCarro={carro.map((l) => l.productoId)}
        onElegir={agregar}
        tipo={tipo}
      />

      <EscanerCodigo
        abierto={escaner}
        onCerrar={() => setEscaner(false)}
        onLeer={(codigo) => {
          const p = productos.find(
            (x) => x.codigoBarras === codigo || x.codigo.toLowerCase() === codigo.toLowerCase(),
          )
          setEscaner(false)
          if (p) agregar(p.id)
          else avisar(`Ningún producto tiene el código ${codigo}.`, 'error')
        }}
      />

    </>
  )
}

function Resumen({
  total,
  costo,
  utilidad,
  items,
  iva,
}: {
  total: number
  costo?: number
  utilidad?: number
  items: number
  /** Solo se pasa en ventas a empresa: los precios guardados son netos
   *  y la factura sale con IVA, así que hay que mostrar los dos. */
  iva?: number
}) {
  return (
    <div className="space-y-2.5">
      <Fila etiqueta={`${items} ${items === 1 ? 'producto' : 'productos'}`} valor={money(total)} />
      {costo !== undefined && <Fila etiqueta="Costo de la mercadería" valor={money(costo)} tenue />}
      {utilidad !== undefined && (
        <Fila etiqueta="Utilidad estimada" valor={money(utilidad)} tono="text-lima-600 dark:text-lima-300" />
      )}
      <div className="border-t borde-suave pt-3">
        <div className="flex items-baseline justify-between">
          <span className="text-[15px] font-bold texto-base">{iva ? 'Total neto' : 'Total'}</span>
          <span className="cifra font-display text-2xl font-bold texto-base">{money(total)}</span>
        </div>
        {iva !== undefined && (
          <div className="mt-2.5 space-y-2 rounded-2xl superficie-hundida p-3">
            <Fila etiqueta={`IVA ${iva}%`} valor={money(montoIva(total, iva))} tenue />
            <div className="flex items-baseline justify-between">
              <span className="text-[13px] font-bold texto-base">Total con IVA</span>
              <span className="cifra text-[17px] font-bold texto-base">
                {money(conIva(total, iva))}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Fila({
  etiqueta,
  valor,
  tenue,
  tono,
}: {
  etiqueta: string
  valor: string
  tenue?: boolean
  tono?: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn('text-[13px]', tenue ? 'texto-tenue' : 'texto-suave')}>{etiqueta}</span>
      <span className={cn('cifra text-[14px] font-semibold', tono ?? 'texto-base')}>{valor}</span>
    </div>
  )
}

/* ------------------- Portada: a quién le vendo ------------------- */

/** Primer paso de toda venta. No es un detalle de forma: el precio de
 *  un mismo kilo cambia según si el que compra es una pastelería o una
 *  persona, así que la pantalla de venta no se puede armar bien hasta
 *  que esto esté resuelto. Debajo de cada cuadro va el simulador, para
 *  poder cotizar sin empezar una venta. */
function PortadaVenta({ onElegir }: { onElegir: (tipo: TipoCliente) => void }) {
  const [simulador, setSimulador] = useState<TipoCliente | null>(null)

  return (
    // El menú flotante de abajo tapa el final de la página en teléfono:
    // el margen extra deja que el segundo simulador se alcance a ver.
    <Pagina className="pb-28 lg:pt-6 lg:pb-8">
      <div className="grid gap-5 escalonar sm:grid-cols-2">
        <div>
          <PuertaTipo
            titulo="Empresa"
            detalle="Pastelerías, hoteles y restoranes"
            pie="Precios mayoristas + IVA"
            icono={<IconoPuestoEmpresa className="size-9" />}
            tono="from-tinta-500 via-tinta-600 to-tinta-700"
            resplandor="bg-tinta-300"
            onClick={() => onElegir('empresa')}
          />
          <BotonSimulador tono="tinta" onClick={() => setSimulador('empresa')} />
        </div>

        <div>
          <PuertaTipo
            titulo="Particular"
            detalle="Para la casa, al detalle"
            pie="Precios al detalle"
            icono={<IconoCanastoParticular className="size-9" />}
            tono="from-magenta-500 via-magenta-600 to-magenta-700"
            resplandor="bg-magenta-300"
            onClick={() => onElegir('particular')}
          />
          <BotonSimulador tono="magenta" onClick={() => setSimulador('particular')} />
        </div>
      </div>

      <SimuladorPrecios tipo={simulador} onCerrar={() => setSimulador(null)} />
    </Pagina>
  )
}

function PuertaTipo({
  titulo,
  detalle,
  pie,
  icono,
  tono,
  resplandor,
  onClick,
}: {
  titulo: string
  detalle: string
  pie: string
  icono: React.ReactNode
  tono: string
  resplandor: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex w-full flex-col overflow-hidden rounded-[28px] bg-gradient-to-br p-6 text-left text-white sm:p-7',
        'transition-all duration-300 ease-[var(--ease-suave)] hover:-translate-y-1 hover:shadow-2xl active:translate-y-0 active:duration-100',
        tono,
      )}
    >
      {/* Resplandor de fondo: el color propio de cada camino, difuminado
          como un fruto fuera de foco detrás del mesón. Sube de tamaño al
          pasar el mouse, como si se acercara. */}
      <span
        aria-hidden
        className={cn(
          'absolute -top-10 -right-10 size-40 rounded-full opacity-25 blur-3xl transition-transform duration-500 group-hover:scale-125',
          resplandor,
        )}
      />
      <span
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_14%_120%,rgba(255,255,255,0.16),transparent_55%)]"
      />

      <div className="relative flex items-start justify-between gap-3">
        <span className="grid size-16 shrink-0 place-items-center rounded-2xl bg-white/15 shadow-inner shadow-black/10 backdrop-blur-sm ring-1 ring-white/25 transition-transform duration-300 group-hover:scale-105 group-hover:-rotate-2">
          {icono}
        </span>
        <ArrowRight className="mt-1.5 size-5 shrink-0 text-white/70 transition-all duration-300 group-hover:translate-x-1 group-hover:text-white" />
      </div>

      <div className="relative mt-5">
        <h3 className="font-display text-[28px] leading-none font-bold tracking-tight sm:text-[30px]">
          {titulo}
        </h3>
        <p className="mt-1.5 text-[13px] text-white/75">{detalle}</p>
      </div>

      <div className="relative mt-5 flex items-center gap-2 border-t border-white/15 pt-3.5">
        <span className="size-1.5 shrink-0 rounded-full bg-white/60" />
        <span className="text-[12px] font-semibold tracking-wide text-white/85 uppercase">{pie}</span>
      </div>
    </button>
  )
}

/* ---------------------- Selector de producto ---------------------- */

function SelectorProducto({
  abierto,
  onCerrar,
  onElegir,
  enCarro,
  tipo,
}: {
  abierto: boolean
  onCerrar: () => void
  onElegir: (id: string) => void
  enCarro: string[]
  tipo: TipoCliente
}) {
  const { productos, ajustes } = useDatos()
  const [texto, setTexto] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (abierto) {
      setTexto('')
      setTimeout(() => inputRef.current?.focus(), 80)
    }
  }, [abierto])

  const lista = useMemo(() => {
    const q = normalizar(texto.trim())
    return productos
      .filter((p) => p.activo)
      .filter(
        (p) =>
          !q || [p.nombre, p.codigo, p.codigoBarras].some((c) => c && normalizar(c).includes(q)),
      )
      .sort((a, b) => {
        // Lo que se puede vender primero; lo agotado al final. Entre
        // los que sí se pueden vender, el orden fijo del catálogo.
        const da = a.stock - a.reservado
        const db = b.stock - b.reservado
        if (da <= 0 !== (db <= 0)) return da <= 0 ? 1 : -1
        const oa = a.ordenCatalogo ?? Infinity
        const ob = b.ordenCatalogo ?? Infinity
        return oa !== ob ? oa - ob : a.nombre.localeCompare(b.nombre, 'es')
      })
  }, [productos, texto])

  return (
    <Hoja
      abierta={abierto}
      onCerrar={onCerrar}
      titulo="Agregar productos"
      descripcion={
        tipo === 'empresa'
          ? 'Precios mayoristas, netos (sin IVA).'
          : 'Precios al detalle.'
      }
      ancho="md"
      textoCerrar="OK"
    >
      <div className="sticky top-0 z-10 -mx-5 mb-2 superficie-elevada px-5 pb-3 sm:-mx-6 sm:px-6">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 texto-tenue" />
          <Entrada
            ref={inputRef}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Nombre o código…"
            className="pr-11 pl-11"
          />
          {texto && (
            <button
              onClick={() => setTexto('')}
              className="absolute top-1/2 right-3 grid size-8 -translate-y-1/2 place-items-center rounded-lg texto-tenue"
              aria-label="Borrar"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {lista.length === 0 ? (
        <p className="py-10 text-center text-sm texto-tenue">Ningún producto coincide.</p>
      ) : (
        <ul className="space-y-1 pb-2">
          {lista.map((p) => {
            const disponible = p.stock - p.reservado
            const estado = estadoStock(p, ajustes.stockMinimoGlobal)
            const meta = ESTADO_STOCK_META[estado]
            const yaEsta = enCarro.includes(p.id)
            return (
              <li key={p.id}>
                <button
                  onClick={() => onElegir(p.id)}
                  disabled={disponible <= 0}
                  className="flex w-full items-center gap-3 rounded-2xl p-2.5 text-left transition-colors hover:superficie-hundida disabled:opacity-40"
                >
                  <span className={cn('size-2 shrink-0 rounded-full', meta.punto)} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold texto-base">
                      {p.nombre}
                    </span>
                    <span className="block truncate text-[12px] texto-suave">
                      <span className="codigo">{p.codigo}</span> · {num(disponible)} disponibles ·{' '}
                      <span className="cifra font-semibold">{money(precioLista(p, tipo))}</span>
                      {!tienePrecioPropio(p, tipo) && (
                        <span className="texto-tenue"> · sin precio mayorista</span>
                      )}
                    </span>
                  </span>
                  {yaEsta ? (
                    <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-bosque-500 text-white">
                      <Check className="size-4" strokeWidth={3} />
                    </span>
                  ) : (
                    <span className="grid size-9 shrink-0 place-items-center rounded-xl superficie-hundida texto-suave">
                      <Plus className="size-4" strokeWidth={2.6} />
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Hoja>
  )
}
